import React, { useState, useEffect, useContext } from 'react';
import { Trans } from '@lingui/macro';
import { SelectField, InputField } from '@qover/molecules';
import { Heading4 } from '@qover/typography';
import { LocaleContext } from '@qover/contexts';
import { useFieldValue, useForm } from '@qover/hooks';
import { FormRow } from './policyholders/address.styles';
import { withI18n, withI18nProps } from '@lingui/react';

type CountryOptions = {
  countryOptions: React.OptionHTMLAttributes<any>[];
};

export type AddressProps = {
  autoCompletePrefix?: string;
  disabledFields?: {
    country?: boolean;
    zip?: boolean;
  };
  hideTitle?: boolean;
  initialCountry?: string;
  prefix?: string;
  title?: React.ReactNode;
  zipCodeMaxLength?: number;
  fetchCities: (zip: string) => Promise<string[]>;
};

export const AddressCustom: React.FC<AddressProps & CountryOptions> = props => {
  const { autoCompletePrefix = 'home', zipCodeMaxLength = 4 } = props;
  const prefixVal = props.prefix ? `${props.prefix}.` : '';

  const { language } = useContext(LocaleContext);
  const [cities, setCities] = useState<string[]>([]);
  const zip = useFieldValue<string>(`${prefixVal}zip`);
  const form = useForm();

  const handleCities = (zip: string) => {
    const zipCode = zip?.trim();
    form.mutators.setFieldData(`${prefixVal}zip`, {
      customError: undefined,
    });

    if (zipCode && zipCode.length > 3) {
      props
        .fetchCities?.(zipCode)
        .then(resp => {
          if (resp) {
            setCities(resp);
            form.change(`${prefixVal}city`, resp[0]);
          } else {
            setCities([]);
            form.change(`${prefixVal}city`, '');
            form.mutators.setFieldData(`${prefixVal}zip`, {
              customError: <Trans id="general.invalidZip" />,
            });
          }
        })
        .catch(() => {
          form.mutators.setFieldData(`${prefixVal}zip`, {
            customError: <Trans id="general.invalidZip" />,
          });
          setCities([]);
          form.change(`${prefixVal}city`, '');
          form.resetFieldState(`${prefixVal}city`);
        });
    }
  };

  useEffect(() => handleCities(zip), [language, zip]);

  return (
    <>
      {!props.hideTitle && (
        <Heading4 className="text-left">
          {props.title || <Trans id="general.address" defaults="Address" />}
        </Heading4>
      )}
      <FormRow className="weightLeft">
        <InputField
          autoComplete={`${autoCompletePrefix} street-address`}
          data-test={`${prefixVal}input.street`}
          id={`${prefixVal}street`}
          label={<Trans id="general.street" defaults="Street" />}
          name={`${prefixVal}street`}
        />
        <InputField
          data-test={`${prefixVal}input.number`}
          id={`${prefixVal}number`}
          label={<Trans id="general.number" defaults="Number" />}
          name={`${prefixVal}number`}
          type="text"
        />
      </FormRow>
      <FormRow className="weightRight">
        <InputField
          autoComplete={`${autoCompletePrefix} postal-code`}
          data-test={`${prefixVal}input.postcode`}
          disabled={props.disabledFields?.zip}
          id={`${prefixVal}zip`}
          label={<Trans id="general.postcode" defaults="Postcode" />}
          maxLength={zipCodeMaxLength}
          name={`${prefixVal}zip`}
          type="text"
        />
        <SelectField
          data-test={`${prefixVal}input.city`}
          id={`${prefixVal}city`}
          label={<Trans id="general.city" defaults="City" />}
          name={`${prefixVal}city`}
          options={cities.map(city => ({ value: city, label: city }))}
        />
      </FormRow>
      <SelectField
        data-test={`${prefixVal}input.country`}
        disabled={props.disabledFields?.country}
        id={`${prefixVal}country`}
        label={<Trans id="general.country" defaults="Country" />}
        name={`${prefixVal}country`}
        options={props.countryOptions}
        initialValue={props.initialCountry}
      />
    </>
  );
};

const AddressBeWrapped: React.FC<AddressProps & withI18nProps> = props => {
  const { i18n, ...addressProps } = props;
  const options = [{ label: i18n._({ id: 'belgium' }), value: 'BE' }];

  return (
    <AddressCustom
      {...addressProps}
      countryOptions={options}
      initialCountry="BE"
      zipCodeMaxLength={4}
    />
  );
};

export const AddressBeCustom = withI18n()(AddressBeWrapped);

function AddressFrWrapped(props: AddressProps & withI18nProps) {
  const { i18n, ...addressProps } = props;
  const options = [{ label: i18n._({ id: 'france' }), value: 'FR' }];

  return (
    <AddressCustom
      {...addressProps}
      countryOptions={options}
      initialCountry="FR"
      zipCodeMaxLength={5}
    />
  );
}

export const AddressFrCustom = withI18n()(AddressFrWrapped);
